/**
 * Model for the AHA integration of a standard course. The backend only knows the AHA environment it is connected to
 * (each environment has its own database), so Local, Dev and Production are always shown, but only one can be acted on.
 */

export const ENV_ORDER = ["local", "dev", "production"];
export const ENV_LABEL = { local: "Local", dev: "Dev", production: "Production" };

/** AHA's own environment names mapped to the console's environments (see AhaEnvironment.fromProfiles). */
const AHA_TO_CONSOLE = { DEV: "local", QA: "dev", PROD: "production" };

export const consoleEnvOf = (ahaEnvironment) => AHA_TO_CONSOLE[ahaEnvironment] || null;

/** The state of one linked product in the connected environment. */
export function productState(product) {
  if (!product.environment || !product.productNumber) return "none";
  if (product.active) return "active";
  return product.verified ? "verified" : "unverified";
}

export const STATE = {
  none: { label: "No number here", short: "No number", tone: "muted", icon: "minus" },
  unverified: { label: "Linked · not verified", short: "Not verified", tone: "unk", icon: "help" },
  verified: { label: "Verified · inactive", short: "Verified", tone: "info", icon: "shield" },
  active: { label: "Active", short: "Active", tone: "ok", icon: "check" },
};

/**
 * What a cell in the grid offers. Only the connected environment can change; the others say where to act.
 * `publishedHere` is false when no version of the course is live in that environment, which blocks activation.
 */
export function cellActions(state, { connected, retired, publishedHere }) {
  if (!connected) return { hint: "Switch to this environment to act" };
  if (retired) return {};
  if (state === "unverified") return { verify: true };
  if (state === "verified") return publishedHere ? { activate: true } : { hint: "Publish a version first" };
  if (state === "active") return { deactivate: true };
  return {};
}

/**
 * The chip for the course list's Integrations column: the worst case across the linked products in the connected
 * environment. A live course whose products are not all active gets the warning tone.
 */
export function summaryChip(summary, course) {
  const live = Boolean(course.currentVersionNumber) && course.status !== "RETIRED";
  const count = summary?.count ?? 0;
  if (!count) return course.category === "AHA" ? { tone: "unk", icon: "minus", text: "AHA · Not linked" } : null;
  const where = ENV_LABEL[consoleEnvOf(summary.environment)] || null;
  if (!where) return { tone: "unk", icon: "help", text: `AHA · ${count} linked` };
  if (summary.active === count) return { tone: "ok", icon: "check", text: `AHA · Live in ${where}` };
  if (summary.active > 0) return { tone: live ? "warn" : "muted", icon: "alert", text: `AHA · ${summary.active} of ${count} live in ${where}` };
  if (summary.verified > 0) return { tone: live ? "warn" : "muted", icon: live ? "alert" : "minus", text: `AHA · Not active in ${where}` };
  return { tone: "unk", icon: "help", text: `AHA · Not verified in ${where}` };
}

/** True when a live course has products that are not all active here: the list shows a count of these. */
export function hasGap(summary, course) {
  const chip = summaryChip(summary, course);
  return chip?.tone === "warn";
}
